import { useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Form,
  InputGroup,
  Spinner,
  Table,
} from "react-bootstrap";
import Swal from "sweetalert2";
import {
  issueMcpCredential,
  listMcpCredentials,
  mcpError,
  revokeMcpCredential,
} from "@/api/mcp.js";

const fecha = (value) =>
  value
    ? new Date(value).toLocaleString("es-MX", {
        dateStyle: "short",
        timeStyle: "short",
      })
    : "—";

/**
 * Tus llaves de acceso al MCP.
 *
 * Tuyas de verdad: el servidor sólo devuelve las del contador que pregunta, así
 * que aquí no aparecen las de los compañeros ni las tuyas en su pantalla. Por
 * eso tampoco hay columna de "a nombre de": siempre serías tú.
 *
 * La llave recién emitida se enseña una sola vez, en un aviso que no se va
 * solo: el servidor guarda únicamente su huella, así que si esta pantalla se
 * cierra sin copiarla, la llave se perdió y hay que emitir otra.
 */
const McpCredentials = () => {
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [issuing, setIssuing] = useState(false);
  const [issued, setIssued] = useState(null);

  const load = () =>
    listMcpCredentials()
      .then(setCredentials)
      .catch((error) =>
        Swal.fire(
          "Error",
          mcpError(error, "No se pudieron leer las llaves."),
          "error",
        ),
      )
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const issue = async () => {
    if (!name.trim()) return;
    setIssuing(true);
    try {
      const credential = await issueMcpCredential(name.trim());
      setIssued(credential);
      setName("");
      await load();
    } catch (error) {
      Swal.fire("No se emitió", mcpError(error), "error");
    } finally {
      setIssuing(false);
    }
  };

  const revoke = async (credential) => {
    const answer = await Swal.fire({
      title: "¿Revocar la llave?",
      text: `"${credential.name}" dejará de entrar en cuanto aceptes. No se puede deshacer.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, revocar",
      cancelButtonText: "Cancelar",
    });

    if (!answer.isConfirmed) return;

    try {
      await revokeMcpCredential(credential.id);
      await load();
    } catch (error) {
      Swal.fire("No se revocó", mcpError(error), "error");
    }
  };

  return (
    <Card className="mb-3">
      <Card.Header>Llaves de acceso</Card.Header>
      <Card.Body>
        <p className="text-muted">
          Estas llaves son tuyas: entran con tus permisos y sólo tú las ves.
          Ponle a cada una un nombre que diga para qué es —el cliente donde la
          vas a pegar—, porque después es lo único que las distingue.
        </p>

        {issued && (
          <Alert variant="success" onClose={() => setIssued(null)} dismissible>
            <Alert.Heading className="h6">
              Cópiala ahora: no se vuelve a mostrar
            </Alert.Heading>
            <code className="d-block user-select-all text-break my-2">
              {issued.token}
            </code>
            <span className="small">
              El servidor sólo guarda su huella. Si la pierdes, revócala y emite
              otra.
            </span>
          </Alert>
        )}

        <InputGroup className="mb-3">
          <Form.Control
            placeholder="Nombre de la llave (por ejemplo: Claude de escritorio)"
            value={name}
            maxLength={100}
            onChange={(event) => setName(event.target.value)}
          />
          <Button
            variant="primary"
            onClick={issue}
            disabled={issuing || !name.trim()}
          >
            {issuing ? "Emitiendo…" : "Emitir llave"}
          </Button>
        </InputGroup>

        {loading ? (
          <div className="d-flex align-items-center gap-2">
            <Spinner animation="border" size="sm" />
            <span>Cargando llaves…</span>
          </div>
        ) : credentials.length === 0 ? (
          <p className="text-muted mb-0">
            Todavía no hay ninguna llave emitida.
          </p>
        ) : (
          <Table responsive hover size="sm" className="align-middle mb-0">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Llave</th>
                <th>Emitida</th>
                <th>Último uso</th>
                <th>Estado</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {credentials.map((credential) => (
                <tr key={credential.id}>
                  <td>{credential.name}</td>
                  <td>
                    <code>{credential.prefix}…</code>
                  </td>
                  <td>{fecha(credential.createdAt)}</td>
                  <td>
                    {credential.lastUsedAt
                      ? fecha(credential.lastUsedAt)
                      : "sin usar"}
                  </td>
                  <td>
                    {credential.active ? (
                      <Badge bg="success">activa</Badge>
                    ) : (
                      <Badge bg="secondary">
                        revocada {fecha(credential.revokedAt)}
                      </Badge>
                    )}
                  </td>
                  <td className="text-end">
                    {credential.active && (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => revoke(credential)}
                      >
                        Revocar
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
};

export default McpCredentials;
